import { OperatorType } from "~/pages/space/workspaces/components/ConditionBuilder/types";
import { CreateWorkspaceFormInput } from "~/pages/space/workspaces/components/CreateWorkspace/CreateWorkspacePage";
import { Control, Path, useFormContext } from "react-hook-form";
import {
  WorkspaceSelectionConditionKeyValueOperator,
  WorkspaceSelectionConditionNumericOperator,
  WorkspaceSelectionConditionOperator,
  WorkspaceSelectionConditionRatingOperator,
  WorkspaceSelectionConditionStringOperator,
} from "~/operations";
import {
  getIsIntKeySelected,
  getIsKeyValueKeySelected,
  getIsRatingKeySelected,
  getIsStringKeySelected,
} from "~/pages/space/workspaces/utils";

type UseOperatorParams = {
  selectionIndex: number;
  conditionIndex: number;
  type?: OperatorType;
};

type OperatorEnums =
  | WorkspaceSelectionConditionOperator
  | WorkspaceSelectionConditionNumericOperator
  | WorkspaceSelectionConditionRatingOperator
  | WorkspaceSelectionConditionStringOperator
  | WorkspaceSelectionConditionKeyValueOperator;

type UseOperatorParamsReturn = {
  name: Path<CreateWorkspaceFormInput>;
  defaultValue: OperatorEnums;
  control: Control<CreateWorkspaceFormInput>;
  type: OperatorType;
};

export function useWorkspaceConditionOperator({
  selectionIndex,
  conditionIndex,
  type: typeFromProps,
}: UseOperatorParams): UseOperatorParamsReturn {
  const { control, watch } = useFormContext<CreateWorkspaceFormInput>();
  const conditionValue = watch(
    `selections.${selectionIndex}.conditions.${conditionIndex}`,
  );

  const isIntKeySelected = getIsIntKeySelected(conditionValue?.formKey);
  const isStringKeySelected = getIsStringKeySelected(conditionValue?.formKey);
  const isRatingKeySelected = getIsRatingKeySelected(conditionValue?.formKey);
  const isKeyValueKeySelected = getIsKeyValueKeySelected(
    conditionValue?.formKey,
  );

  const typeNameMap: Record<OperatorType, Path<CreateWorkspaceFormInput>> = {
    string: `selections.${selectionIndex}.conditions.${conditionIndex}.stringCondition.operator`,
    condition: `selections.${selectionIndex}.conditions.${conditionIndex}.operator`,
    numeric: `selections.${selectionIndex}.conditions.${conditionIndex}.intCondition.operator`,
    rating: `selections.${selectionIndex}.conditions.${conditionIndex}.ratingCondition.operator`,
    keyValue: `selections.${selectionIndex}.conditions.${conditionIndex}.keyValueCondition.operator`,
  };

  const typeDefaultValueMap: Record<OperatorType, OperatorEnums> = {
    string: WorkspaceSelectionConditionStringOperator.Equal,
    condition: WorkspaceSelectionConditionOperator.And,
    numeric: WorkspaceSelectionConditionNumericOperator.Equal,
    rating: WorkspaceSelectionConditionRatingOperator.Equal,
    keyValue: WorkspaceSelectionConditionKeyValueOperator.Contains,
  };

  function getOperatorType(): OperatorType {
    if (isIntKeySelected) return "numeric";
    if (isStringKeySelected) return "string";
    if (isRatingKeySelected) return "rating";
    if (isKeyValueKeySelected) return "keyValue";

    return "condition";
  }

  const type = typeFromProps || getOperatorType();

  return {
    name: typeNameMap[type],
    defaultValue: typeDefaultValueMap[type],
    control,
    type,
  };
}
